import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, Copy, RefreshCw, Trash2, ChevronDown, Loader, Check, Share2 } from "lucide-react";
import type { RecordActionsProps } from "../types/recordHeader.types";
import { useTranslation } from "react-i18next";

export const RecordActions = ({
  record,
  onCopy,
  onDownload,
  onReprocess,
  onDelete,
  onShare,
  copying,
  generatingDoc,
  isCompleted,
  onDocumentDownload,
  disabled = false
}: RecordActionsProps) => {
  const { t } = useTranslation();

  return (
    <div className={`mt-4 pt-4 border-t border-border/30 bg-gradient-to-r from-muted/20 to-transparent rounded-lg -mx-2 px-2 py-2 flex flex-wrap gap-2 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" disabled={generatingDoc || disabled}>
            {generatingDoc ? (
              <Loader size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Download size={14} className="mr-1.5" />
            )}
            <span className="hidden sm:inline">
              {generatingDoc ? t('records.actions.generating') : t('records.actions.download')}
            </span>
            <ChevronDown size={12} className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => onDocumentDownload?.("docx")}
            disabled={!onDocumentDownload || disabled}
          >
            {t('records.actions.download_docx')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDocumentDownload?.("pdf")}
            disabled={!onDocumentDownload || disabled}
          >
            {t('records.actions.download_pdf')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDocumentDownload?.("html")}
            disabled={!onDocumentDownload || disabled}
          >
            {t('records.actions.download_html')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onDocumentDownload?.("odt")}
            disabled={!onDocumentDownload || disabled}
          >
            {t('records.actions.download_odt')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDownload} disabled={disabled}>
            {t('records.actions.download_txt')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        size="sm"
        variant="outline"
        onClick={onCopy}
        disabled={copying || disabled}
      >
        {copying ? (
          <>
            <Check size={14} className="mr-1.5 text-green-500" />
            <span className="hidden sm:inline">{t('records.actions.copied')}</span>
          </>
        ) : (
          <>
            <Copy size={14} className="mr-1.5" />
            <span className="hidden sm:inline">{t('records.actions.copy')}</span>
          </>
        )}
      </Button>

      {isCompleted && onShare && (
        <Button size="sm" variant="outline" onClick={onShare} disabled={disabled}>
          <Share2 size={14} className="mr-1.5" />
          <span className="hidden sm:inline">{t('records.actions.share')}</span>
        </Button>
      )}

      {isCompleted && (
        <Button size="sm" variant="outline" onClick={onReprocess} disabled={disabled}>
          <RefreshCw size={14} className="mr-1.5" />
          <span className="hidden sm:inline">{t('records.actions.reprocess')}</span>
        </Button>
      )}

      <Button
        size="sm"
        variant="outline"
        className="text-red-600 hover:text-red-700 hover:bg-red-50"
        onClick={onDelete}
        disabled={disabled}
      >
        <Trash2 size={14} className="mr-1.5" />
        <span className="hidden sm:inline">{t('records.actions.delete')}</span>
      </Button>
    </div>
  );
};